import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ModalPane} from 'constants/SampleConstants';
import HelpButton from 'help/HelpButton';
import {LocationCapacityConfigurationParams} from 'samples/LocationDAO';
import LocationSchemaSelector from 'samples/LocationSchemaSelector';
import EditableLocationAllowedChildSchemasField from 'samples/storables/editable-fields/EditableLocationAllowedChildSchemasField';
import {EditableLocationCapacityField} from 'samples/storables/editable-fields/EditableLocationCapacityField';
import {LocationSchema} from 'samples/storables/StorableSchemaConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputChangeEvent} from 'util/Types';

import BaseStorablePane, {
  BaseStorablePaneProps,
  CreateStorablePaneState,
  DEFAULT_CREATE_STORABLE_STATE,
} from './BaseStorablePane';

export interface CreateLocationPaneProps
  extends Omit<BaseStorablePaneProps, 'nameLabel' | 'modalPane' | 'schemaSelector'>,
    Required<LocationCapacityConfigurationParams> {
  selectedSchema?: LocationSchema | null;
}

export interface CreateLocationPaneState extends CreateStorablePaneState {
  selectedSchema: LocationSchema | null;
  allowsNonLocationChildren: boolean;
  allowedChildStorableSchemaApiIds: Array<string> | null;
  capacity: number | null;
  hasCapacityError?: boolean;
}

export const DEFAULT_CREATE_LOCATION_PANE_STATE: CreateLocationPaneState = {
  ...DEFAULT_CREATE_STORABLE_STATE,
  allowsNonLocationChildren: true,
  allowedChildStorableSchemaApiIds: null,
  capacity: null,
};

class CreateLocationPane extends React.Component<CreateLocationPaneProps> {
  _handleChangeLocationSchema = (locationSchema: LocationSchema): void => {
    this.props.onUpdate({
      selectedSchema: locationSchema,
      tags: TagUtil.getInitialTags(locationSchema.fields),
    });
  };

  private handleUpdateAllowsNonLocationChildren = (allowsNonLocationChildren: boolean): void => {
    this.props.onUpdate({
      allowsNonLocationChildren,
    });
  };

  private handleUpdateAllowedChildSchemas = (
    allowedChildStorableSchemaApiIds: Array<string> | null
  ): void => {
    this.props.onUpdate({
      allowedChildStorableSchemaApiIds,
    });
  };

  private handleUpdateCapacity = (capacity: number | null): void => {
    this.props.onUpdate({
      capacity,
    });
  };

  private handleUpdateHasCapacityError = (hasCapacityError?: boolean): void => {
    this.props.onUpdate({hasCapacityError});
  };

  _renderSchemaSelector = (): JSX.Element => (
    <LocationSchemaSelector
      error={this.props.validationErrors.schemaError}
      onChange={this._handleChangeLocationSchema}
      registryId={this.props.registryId}
      selectedLocationSchemaId={this.props.selectedSchema ? this.props.selectedSchema.id : null}
    />
  );

  private renderCapacityHelpButton = (helpText: string): JSX.Element => (
    <HelpButton
      renderContent={() => <span>{helpText}</span>}
      iconClassName="u-mini-spacing-left"
      interaction={FloatingInteraction.HOVER}
    />
  );

  private renderCapacityFields = (): JSX.Element => (
    <div className={css(styles.nestedBox)}>
      <CheckboxInput
        checked={this.props.allowsNonLocationChildren}
        onChange={(e: InputChangeEvent) => {
          this.handleUpdateAllowsNonLocationChildren(e.target.checked);
        }}
        id="allow-non-location-children"
        label="Allow storage of plates, boxes, and containers in this location"
      />
      {this.props.allowsNonLocationChildren && (
        <div className={css(styles.capacityConfig)}>
          <div className={css(styles.column)}>
            <div>
              <FieldLabel label="Allowed inventory schemas" labelStyle={FieldLabel.STYLES.LABEL} />
              {this.renderCapacityHelpButton(
                'The plate, box, and container schemas that the location can store. If left empty, there are no restrictions on which schemas can be stored.'
              )}
            </div>
            <EditableLocationAllowedChildSchemasField
              selectedSchemaIds={this.props.allowedChildStorableSchemaApiIds || []}
              onSetSelectedSchemaIds={this.handleUpdateAllowedChildSchemas}
              registryId={this.props.selectedSchema?.registryId}
              isForCreate={true}
            />
          </div>
          <div className={css(styles.column)}>
            <div>
              <FieldLabel label="Inventory capacity" labelStyle={FieldLabel.STYLES.LABEL} />
              {this.renderCapacityHelpButton(
                'The number of plates, boxes, and containers that the location can hold. If left empty, there is no restriction on the number of items.'
              )}
            </div>
            <EditableLocationCapacityField
              capacity={this.props.capacity}
              onChange={this.handleUpdateCapacity}
              isForCreate={true}
              onChangeHasCapacityError={this.handleUpdateHasCapacityError}
            />
          </div>
        </div>
      )}
    </div>
  );

  override render(): JSX.Element {
    return (
      <BaseStorablePane
        nameLabel="Location name"
        modalPane={ModalPane.CREATE_LOCATION}
        schemaSelector={this._renderSchemaSelector()}
        shouldShowProjectSelector={false}
        {...this.props}
      >
        {this.renderCapacityFields()}
      </BaseStorablePane>
    );
  }
}

const styles = StyleSheet.create({
  nestedBox: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    padding: 10,
    marginBottom: 15,
  },
  capacityConfig: {
    marginTop: 8,
    display: 'flex',
    gap: 24,
  },
  column: {
    flex: 1,
  },
});

export default CreateLocationPane;
